/**
 * OC Chat Panel Component
 * Wrapper around ChatPanel that handles rendering based on authentication state
 * Separated from OcFabWidget for better organization
 */

import { Alert } from '@hl/ui-components';
import { FC } from 'react';
import { ThemeProvider } from 'styled-components';

import { IAppTheme } from '../../../../styles/IAppTheme';
import { OcChatConfig } from '../../../../types/oc';
import ChatBackgroundSVG from '../../../BannerSVG/ChatBackgroundSVG';
import { ChatPanel } from '../../../ChatPanel/ChatPanel';
import { LoadingEllipsis } from '../../../Message/LoadingEllipsis';
import { ErrorWrapper } from '../../../TranscriptBox/TranscriptMessages.styled';
import { isAuthError } from '../../config/ocDefaults';
import { useOcFabWidgetOptions } from '../../contexts/OcFabWidgetOptionsContext';
import { useAiAssistantEndpoints, useMockedAiAssistantEndpoints } from '../../hooks/useAiAssistantEndpoints';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { useSessionManagerContext } from '../../sessionManagement/SessionManagerContext';
import { OcLoginRequired } from '../LoginRequired/OcLoginRequired';
import { OcChatProviders } from '../Providers/OcChatProviders';
import WithVerticalSources from '../Sources/WithVerticalSources';
import ChatInnerWithHistory from './ChatInnerWithHistory';
import ChatInnerWithoutHistory from './ChatInnerWithoutHistory';
import { LoadingContainer } from './ChatPanelContent.styled';
import PanelChatHeader from './PanelChatHeader';

export type AuthState =
  | 'checking'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

export interface OcChatPanelProps {
  config: OcChatConfig;
  userId: string;
  authState: AuthState;
  error: Error | null;
  theme: IAppTheme;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * OcChatPanel Component
 *
 * Renders the appropriate content for the chat panel based on authentication state:
 * - Loading skeleton while checking authentication
 * - Login prompt for unauthenticated users
 * - Error message for non-auth errors
 * - Full chat interface for authenticated users
 */
export const OcChatPanel: FC<OcChatPanelProps> = ({
  config,
  userId,
  authState,
  error,
  theme,
  isOpen,
  onClose,
}) => {
  const {
    previewState,
    useSVGBackground,
    showChatHistory,
    panelBaseWidth,
    panelMinWidth,
    panelMaxWidth } = useOcFabWidgetOptions();

  // Call hooks at top level (before any conditional returns)
  // Get renameSessionIfNeeded from session context for first message labeling
  const { renameSessionIfNeeded, initialSessionKey, chatContextRef } =
    useSessionManagerContext();

  // Use mocked endpoints in preview mode, otherwise use production endpoints
  const isPreviewMode = config.mode === 'preview';
  // Always call both hooks unconditionally to satisfy React hooks rules
  const productionEndpoints = useAiAssistantEndpoints(config, renameSessionIfNeeded);
  const mockedEndpoints = useMockedAiAssistantEndpoints(previewState, renameSessionIfNeeded);

  const endpoints = isPreviewMode ? mockedEndpoints : productionEndpoints;

  const appConfig = {
    systemId: config.aiAssistantKey,
    tenantId: config.tenantId,
    userId,
  };

  // Detect narrow screen for responsive behavior
  const isNarrow = useIsNarrow();

  // Common ChatPanel props used across all states
  const commonPanelProps = {
    show: isOpen,
    onHide: onClose,
    initialWidth: Math.min(
      panelBaseWidth,
      window.document.body.getBoundingClientRect().width,
    ),
    minWidth: panelMinWidth,
    maxWidth: panelMaxWidth,
  };

  // Render loading state
  if (authState === 'checking') {
    // override loading ellipsis color to match chat panel text color to ensure it's visible
    const loadingTheme = {...theme, color: { ...theme.color, loadingEllipses: theme.color.chatPanel }};
    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <ThemeProvider theme={loadingTheme}>
        <LoadingContainer>
          <LoadingEllipsis />
        </LoadingContainer>
        </ThemeProvider>
      </ChatPanel>
    );
  }

  // Render login required
  if (
    authState === 'unauthenticated' ||
    (authState === 'error' && error && isAuthError(error))
  ) {
    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <OcLoginRequired
          loginUrl={config.loginUrl}
        />
      </ChatPanel>
    );
  }

  // Render error state (non-auth errors)
  if (authState === 'error' && error) {
    const errorMessage =
      'An error occurred. Reloading the page may fix the issue.';

    return (
      <ChatPanel className="hl-oc-chat-content" {...commonPanelProps}>
        <ErrorWrapper>
          {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
          <Alert danger={true}>{errorMessage}</Alert>
        </ErrorWrapper>
      </ChatPanel>
    );
  }

  // Authenticated - render full chat interface
  return (
    <ChatPanel
      className="hl-oc-chat-content"
      {...commonPanelProps}
      customBackground={useSVGBackground && <ChatBackgroundSVG />}
    >
      <OcChatProviders
        endpoints={endpoints}
        appConfig={appConfig}
        config={config}
        theme={theme}
        initialSessionKey={initialSessionKey ?? undefined}
        chatContextRef={chatContextRef}
      >
        <WithVerticalSources>
          <PanelChatHeader />
          {showChatHistory && (
            <ChatInnerWithHistory
              isNarrow={isNarrow}
            />
          )}
          {!showChatHistory && (
            <ChatInnerWithoutHistory
              isNarrow={isNarrow}
            />
          )}
        </WithVerticalSources>
      </OcChatProviders>
    </ChatPanel>
  );
};

export default OcChatPanel;
